package com.evoapps.evoshift.photo

import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import com.evoapps.evoshift.domain.usecase.user.ProfilePhoto

/**
 * The platform's photo pickers (SH-94): Android's Photo Picker and camera, iOS's PHPicker and
 * camera, the browser's file chooser. A picked photo arrives as its file's bytes (read up to
 * [ProfilePhoto.MAX_INPUT_BYTES] + 1, so a huge file is refused without being read whole);
 * closing a picker sends nothing.
 */
interface ImagePicker {
    /** Whether this device has a camera the app can use ([takePhoto]). */
    val canTakePhoto: Boolean

    fun pickFromGallery()

    fun takePhoto()
}

/** Called with the picked file's bytes, or with null when it could not be read. */
typealias OnImagePicked = (ByteArray?) -> Unit

@Composable
internal expect fun rememberPlatformImagePicker(onPicked: OnImagePicked): ImagePicker

/**
 * Replaces the platform pickers where there is no system UI (UI tests): given the callback, it
 * returns the picker the screen uses.
 */
val LocalImagePickerOverride = staticCompositionLocalOf<((OnImagePicked) -> ImagePicker)?> { null }

/** The picker for this platform, or the [LocalImagePickerOverride] when one is provided. */
@Composable
fun rememberImagePicker(onPicked: OnImagePicked): ImagePicker {
    val override = LocalImagePickerOverride.current
    return override?.invoke(onPicked) ?: rememberPlatformImagePicker(onPicked)
}

/** At most [ProfilePhoto.MAX_INPUT_BYTES] + 1 bytes are kept: enough to know a file is too large. */
internal const val PICK_READ_LIMIT = ProfilePhoto.MAX_INPUT_BYTES + 1

/** The image files the desktop's Open dialog offers (the browser's chooser offers any image). */
internal val IMAGE_EXTENSIONS = setOf("jpg", "jpeg", "png", "gif", "webp", "heic", "heif")

/** SH-233: what the profile photo takes when a picture is dropped on it: any image, as the pickers. */
val photoDropSpec = com.evoapps.evoshift.filedrop.FileDropSpec(
    extensions = IMAGE_EXTENSIONS,
    readLimit = PICK_READ_LIMIT,
    mimeTypes = setOf("image/*")
)
